import { Fragment } from 'preact/jsx-runtime';
import { useComputed } from '@preact/signals';
import { useCallback } from 'preact/hooks';
import type { Product } from '@/domain/Product';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';
import type { Options } from '../../config';
import { type PersistedState } from '../../Store.ts';
import ViewAlternativesDrawer from '../../drawers/ViewAlternativesDrawer/ViewAlternativesDrawer.tsx';
import useCardModalId from '../hooks/useCardModalId.tsx';
import QuickViewDrawer from '../../drawers/QuickViewDrawer/QuickViewDrawer.tsx';
import { trackProductClick } from '../../Tracking.ts';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import navigateToProductUrl from '@/widgets/utils/navigateToProductUrl.ts';

type CardDrawersProps = {
  product: Product;
  productUrl: string;
  trackingPosition: number;
  trackingList: string;
};

export default function CardDrawers({
  product,
  productUrl,
  trackingPosition,
  trackingList
}: CardDrawersProps) {
  const {
    discoveryOfferBundleProducts,
    financialSettings: { defaultCurrency }
  } = useMarketConfig();
  const [{ openModal }, setState] = useQueryStringStore<PersistedState>();
  const { enableProductCardQuickView } = useWidgetOptions<Options>();
  const quickViewModalId = useCardModalId<PersistedState['openModal']>(
    'quick-view-',
    product.id
  );

  const viewAlternativesModalId = useCardModalId<PersistedState['openModal']>(
    'view-alternatives-',
    product.id
  );

  const isQuickViewOpen = useComputed(
    () => openModal.value === quickViewModalId.value
  );
  const isViewAlternativesOpen = useComputed(
    () => openModal.value === viewAlternativesModalId.value
  );

  const closeQuickViewDrawer = useCallback(() => {
    setState({ openModal: undefined });
  }, [setState]);

  const closeShowAlternativesDrawer = useCallback(() => {
    setState({ openModal: undefined });
  }, [setState]);

  const handleGoToProductPageClick = (e: MouseEvent) => {
    trackProductClick(
      product,
      trackingPosition,
      discoveryOfferBundleProducts.includes(product.id),
      defaultCurrency,
      trackingList,
      'plp quick view'
    );

    navigateToProductUrl(e, productUrl);
  };

  return (
    <Fragment>
      {enableProductCardQuickView && (
        <QuickViewDrawer
          product={product}
          isOpen={isQuickViewOpen.value}
          onClose={closeQuickViewDrawer}
          onGoToProductPage={handleGoToProductPageClick}
        />
      )}
      {/* todo - track click event with handleGoToProductPageClick as above  */}
      {!product.inStock && product.alternativeProducts ? (
        <ViewAlternativesDrawer
          isOpen={isViewAlternativesOpen.value}
          onClose={closeShowAlternativesDrawer}
          product={product}
        />
      ) : null}
    </Fragment>
  );
}
